import React, { useEffect, useState } from "react";
import { post } from "../../../lib/api";

// Use the challenge prop if provided, otherwise fallback to a default
const ConnectionsEditComponent = ({ game_id, date, onCancel, challenge, onSaved }) => {
  const CURRENT_SELECTED_CHALLENGE = challenge;
  const [data, setData] = useState(null);
  const [challengeDict, setChallengeDict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [previewValue, setPreviewValue] = useState(null);
  const [saveMessage, setSaveMessage] = useState("");

  useEffect(() => {
    if (!game_id || !date) return;
    setLoading(true);
    setError(null);
    const url = `${process.env.REACT_APP_CMC_API}/statistics?method=getDailyChallengeComplex&gameid=${game_id}&date=${date}`;
    fetch(url, {
      headers: { Connection: "close" },
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to fetch complex challenge data");
        return response.json();
      })
      .then((json) => {
        setData(json);
        // Map to dictionary by challenge_name
        if (json && json.challenge_name && Array.isArray(json.challenge_name)) {
          const dict = {};
          for (let i = 0; i < json.challenge_name.length; i++) {
            const name = json.challenge_name[i];
            dict[name] = {
              game_id: json.game_id?.[i],
              challenge_name: name,
              variant_id: json.variant_id?.[i],
              variant_name: json.variant_name?.[i],
              challenge_data: json.challenge_data?.[i],
            };
          }
          setChallengeDict(dict);
        } else {
          setChallengeDict(null);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [game_id, date]);

  // Keep editValue in sync with the current challenge data unless the user is editing
  useEffect(() => {
    if (challengeDict && challengeDict[CURRENT_SELECTED_CHALLENGE]) {
      setEditValue(challengeDict[CURRENT_SELECTED_CHALLENGE].challenge_data || "");
    }
  }, [challengeDict]);

  // Clear save message after 2 seconds
  useEffect(() => {
    if (saveMessage) {
      const timer = setTimeout(() => {
        setSaveMessage("");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [saveMessage]);

  // Get the current challenge data
  const selectedChallengeData = challengeDict?.[CURRENT_SELECTED_CHALLENGE]?.challenge_data;

  // Use previewValue if set, otherwise use the current challenge data
  const displayData = previewValue !== null ? previewValue : selectedChallengeData;

  // Split challenge_data by newlines, filter out empty lines
  const words = displayData
    ? displayData.split(/\r?\n/).map(w => w.trim()).filter(Boolean)
    : [];

  // Remove leading numbers and replace '\n' with <br /> for display
  // Keep only the *last* digit from the number prefix (e.g. "42-Laang" → "2-Laang")
  // Remove only the first leading digit from each line (e.g. "2TJIL UILENSPIEGEL" → "TJIL UILENSPIEGEL")
  const displayWords = words.map(word => {
    const cleaned = word.replace(/^\d/, ""); // remove the very first digit only

    return cleaned.split(/\\n/).map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 && <br />}
          {line}
        </React.Fragment>
    ));
  });



  // When entering edit mode, set the textarea value to the current challenge data
  const handleEdit = () => {
    setEditValue(selectedChallengeData || "");
    setIsEditing(true);
    setPreviewValue(null);
  };

  // Preview changes by updating the previewValue
  const handlePreview = () => {
    setPreviewValue(editValue);
  };

  // Save changes (send to backend and update local state)
  const handleSave = async () => {
    try {
      const result = await post({
        method: "addChallenge",
        game_id,
        challenge_id: CURRENT_SELECTED_CHALLENGE,
        data: editValue,
        variant_id: 1,
        variant_name: "challenge",
      }, "/challenges");
      if (result.error) {
        alert("Error saving challenge: " + result.error);
        return;
      }
      // Update local state only if successful
      if (challengeDict && challengeDict[CURRENT_SELECTED_CHALLENGE]) {
        setChallengeDict({
          ...challengeDict,
          [CURRENT_SELECTED_CHALLENGE]: {
            ...challengeDict[CURRENT_SELECTED_CHALLENGE],
            challenge_data: editValue,
          },
        });
      }
      setPreviewValue(null);
      setSaveMessage("Changes saved");
    } catch (err) {
      alert("Error saving challenge: " + err.message);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setIsEditing(false);
    setPreviewValue(null);
  };

  // Remove challenge
  const handleRemove = async () => {
    if (!window.confirm("Are you sure you want to delete this challenge?")) {
      return;
    }
    try {
      const result = await post({
        method: "removeChallenge",
        game_id,
        date,
        challenge_name: CURRENT_SELECTED_CHALLENGE,
      }, "/challenges");
      if (result.error) {
        alert("Error removing challenge: " + result.error);
        return;
      }
      setSaveMessage("Challenge removed");
      // Trigger refresh of calendar data
      if (typeof onSaved === 'function') onSaved();
      // Close the editor after a short delay
      setTimeout(() => {
        if (onCancel) onCancel();
      }, 500);
    } catch (err) {
      alert("Error removing challenge: " + err.message);
    }
  };

  if (loading) return <div>Loading complex challenge data...</div>;
  if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;

  return (
    <div className="p-3 rounded-xl mt-2">
      <div className="mt-2">
        {displayWords.length > 0 ? (
          <div className="grid grid-cols-4 gap-1">
            {displayWords.slice(0, 16).map((word, idx) => (
              <div
                key={idx}
                className="rounded-xl bg-[#ffffff] dark:bg-neutral-800 flex items-center justify-center min-h-[48px] font-semibold text-xs text-neutral-700 dark:text-neutral-200 text-center shadow-sm px-2 py-2 select-none"
              >
                {word}
              </div>
            ))}
          </div>
        ) : (
          <div>Not found or no data.</div>
        )}
      </div>
      {/* Edit section: always visible */}
      <div className="mt-4 flex flex-col gap-2">
        <textarea
          className="w-full p-2 border border-gray-300 dark:border-neutral-600 rounded-md bg-[#ffffff] dark:bg-gray-800 dark:text-white text-xs font-mono min-h-[120px] shadow-sm"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
        />
        <div className="flex gap-2 mt-2">
          {/* Preview Changes (horizontal eye icon) */}
          <button
            className="bg-gray-50 hover:bg-gray-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 transition-colors shadow-none p-2 min-w-0 rounded-md flex items-center justify-center"
            title="Preview Changes"
            onClick={handlePreview}
          >
            <svg width="20" height="20" fill="none" stroke="#64748b" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M1 12C3.5 7 8.5 4 12 4s8.5 3 11 8c-2.5 5-7.5 8-11 8s-8.5-3-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
          {/* Save Changes (checkmark icon) */}
          <button
            className="bg-gray-50 hover:bg-gray-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 transition-colors shadow-none p-2 min-w-0 rounded-md flex items-center justify-center"
            title="Save Changes"
            onClick={handleSave}
          >
            <svg width="20" height="20" fill="none" stroke="#22c55e" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>
          </button>
          {/* Cancel (X icon) */}
          <button
            className="bg-gray-50 hover:bg-gray-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 transition-colors shadow-none p-2 min-w-0 rounded-md flex items-center justify-center"
            title="Cancel"
            onClick={onCancel}
          >
            <svg width="20" height="20" fill="none" stroke="#F8A5A5" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
          {/* Remove Challenge (trash icon) */}
          <button
            className="bg-gray-50 hover:bg-gray-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 transition-colors shadow-none p-2 min-w-0 rounded-md flex items-center justify-center"
            title="Remove Challenge"
            onClick={handleRemove}
          >
            <svg width="20" height="20" fill="none" stroke="#ef4444" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </button>
          {/* Save message */}
          {saveMessage && (
            <div className="flex items-center text-green-600 dark:text-green-400 text-sm font-medium">
              {saveMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectionsEditComponent; 